import type { Product } from '@/services/products'

export type StorefrontImageSource = 'block' | 'product_field' | 'gallery' | 'variant' | 'cache' | 'none'

const CACHE_PREFIX = 'sf.pimg.v1:'
const LRU_MAX = 400
/** In-memory LRU: productId → last known good URL (survives re-renders; bounded). */
const memImageLru = new Map<string, string>()

function touchMem(productId: string, url: string) {
  if (memImageLru.has(productId)) memImageLru.delete(productId)
  memImageLru.set(productId, url)
  while (memImageLru.size > LRU_MAX) {
    const k = memImageLru.keys().next().value as string | undefined
    if (k === undefined) break
    memImageLru.delete(k)
  }
}

function readMem(productId: string): string | null {
  const u = memImageLru.get(productId)
  return u != null && String(u).trim() ? String(u).trim() : null
}

export function safeLocalImageGet(productId: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return readMem(productId)
    const v = localStorage.getItem(CACHE_PREFIX + productId)
    const t = v != null ? String(v).trim() : ''
    if (t) {
      touchMem(productId, t)
      return t
    }
  } catch {
    /* private mode */
  }
  return readMem(productId)
}

export function safeLocalImageSet(productId: string, url: string) {
  const u = String(url ?? '').trim()
  if (!u || !productId) return
  touchMem(productId, u)
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(CACHE_PREFIX + productId, u)
  } catch {
    /* quota */
  }
}

/** Warm browser cache / decode without blocking UI. */
export function preloadProductImageUrl(url: string): Promise<void> {
  const u = String(url ?? '').trim()
  if (!u) return Promise.resolve()
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = u
    if (img.complete && img.naturalHeight > 0) resolve()
  })
}

/** Ordered candidates: product fields → gallery table → variant media (deduped). */
export function collectProductImageCandidates(product: Product): { url: string; source: Exclude<StorefrontImageSource, 'block' | 'cache'> }[] {
  const out: { url: string; source: Exclude<StorefrontImageSource, 'block' | 'cache'> }[] = []
  const seen = new Set<string>()
  const add = (url: string, source: Exclude<StorefrontImageSource, 'block' | 'cache'>) => {
    const u = String(url ?? '').trim()
    if (!u || seen.has(u)) return
    seen.add(u)
    out.push({ url: u, source })
  }

  add(String(product.cover_image ?? ''), 'product_field')
  add(String(product.image ?? ''), 'product_field')
  for (const x of product.images ?? []) {
    if (typeof x === 'string') add(x, 'product_field')
    else if (x && typeof x === 'object' && 'url' in x)
      add(String((x as { url?: unknown }).url ?? ''), 'gallery')
  }

  for (const img of product.product_images ?? []) add(String(img.url ?? ''), 'gallery')

  for (const row of product.variant_swatches ?? []) add(String(row.image_url ?? ''), 'variant')
  for (const v of product.variants ?? []) {
    add(String(v.image_url ?? ''), 'variant')
    const hit = v.images?.find((x) => String(x.url ?? '').trim() !== '')
    if (hit?.url) add(String(hit.url), 'variant')
  }
  return out
}

function logImageResolve(productId: string, url: string, source: StorefrontImageSource) {
  if (!import.meta.env.DEV) return
  // eslint-disable-next-line no-console
  console.debug(`[storefront:image] id=${productId} source=${source} url=${url ? `${url.slice(0, 64)}${url.length > 64 ? '…' : ''}` : '∅'}`)
}

/**
 * Resolves primary image: block → live product URLs → localStorage + LRU → none.
 * Persists last known good URL per product for cross-session stability.
 */
export function resolveProductPrimaryImage(
  product: Product,
  opts?: { blockImage?: string }
): { url: string; source: StorefrontImageSource } {
  const block = (opts?.blockImage ?? '').trim()
  if (block) {
    logImageResolve(product.id, block, 'block')
    safeLocalImageSet(product.id, block)
    return { url: block, source: 'block' }
  }

  const candidates = collectProductImageCandidates(product)
  const primary = candidates[0]
  if (primary) {
    logImageResolve(product.id, primary.url, primary.source)
    safeLocalImageSet(product.id, primary.url)
    return { url: primary.url, source: primary.source }
  }

  const cached = safeLocalImageGet(product.id)
  if (cached) {
    logImageResolve(product.id, cached, 'cache')
    return { url: cached, source: 'cache' }
  }

  logImageResolve(product.id, '', 'none')
  return { url: '', source: 'none' }
}
